import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import type { CurrentUser, Role } from '@electrodavisan/shared';
import { api } from '../services/api';

/**
 * Identidad efectiva del usuario.
 *
 * El ID token sirve para pintar la interfaz rapido, pero la fuente de verdad
 * es `/api/me`: es el backend quien dice que roles tiene realmente el token.
 * La interfaz nunca decide permisos por su cuenta.
 */
export function useCurrentUser() {
  const auth = useAuth();

  return useQuery<CurrentUser>({
    queryKey: ['me', auth.user?.profile.sub],
    queryFn: () => api.get<CurrentUser>('/api/me'),
    enabled: auth.isAuthenticated,
    staleTime: 5 * 60_000,
    retry: (failureCount, error) => {
      // No insistir ante 401/403: se resuelve renovando sesion, no reintentando.
      const status = (error as { status?: number }).status;
      if (status === 401 || status === 403) return false;
      return failureCount < 2;
    },
  });
}

export function hasRole(user: CurrentUser | undefined, ...roles: Role[]): boolean {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return user.roles.some((role) => roles.includes(role));
}

/** Iniciales para el avatar del header. */
export function initialsOf(user: CurrentUser | undefined): string {
  if (!user) return '?';
  const source = user.fullName?.trim() || user.email || user.username || '?';
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
